<script setup lang="ts">
import { ref, computed, useTemplateRef, onMounted, onUnmounted, watch } from "vue";
import { UiAside, UiText, UiButton, UiIcon, UiDialog, UiTabs, UiTabsItem, useToaster } from "@shopexperts/shared";
import { useLeads } from "../composables/useLeads";
import { useDeleteLead } from "../composables/useDeleteLead";
import { useMarkLeadResolved } from "../composables/useMarkLeadResolved";
import { useAdminDraftLeads } from "../composables/useAdminDraftLeads";
import { useSubmitDraftLead } from "../composables/useSubmitDraftLead";
import { useDeleteDraftLead } from "../composables/useDeleteDraftLead";
import LeadCard from "../components/LeadCard.vue";
import DraftLeadCard from "../components/DraftLeadCard.vue";
import DraftLeadForm from "../components/DraftLeadForm.vue";
import LeadForm from "../components/LeadForm.vue";
import AssignExpertsForm from "../components/AssignExpertsForm.vue";
import type { DraftLeadAdminResponse, LeadAdminResponse } from "../types";

// Toaster for notifications
const toaster = useToaster();

// Tab state
const activeTab = ref<'pending' | 'all' | 'draft-pending' | 'draft-all'>('pending');

// Status filter based on active tab
const statusFilter = computed(() => {
  return activeTab.value === 'pending' ? 'pending' : undefined;
});

// Fetch leads
const {
  leads,
  isLoading,
  error,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
} = useLeads(statusFilter, { limit: 20 });

// Draft leads status filter
const draftStatusFilter = computed<'pending' | 'submitted' | 'all'>(() => {
  if (activeTab.value === 'draft-pending') return 'pending';
  if (activeTab.value === 'draft-all') return 'all';
  return 'pending'; // default
});

// Fetch draft leads
const { 
  draftLeads, 
  isLoading: isDraftLoading, 
  error: draftError,
  hasNextPage: hasDraftNextPage,
  fetchNextPage: fetchNextDraftPage,
  isFetchingNextPage: isDraftFetchingNextPage,
  refetch: refetchDraftLeads,
  isRefetching: isDraftRefetching,
} = useAdminDraftLeads({ status: draftStatusFilter });

// Draft lead mutations
const { mutateAsync: submitDraft } = useSubmitDraftLead();
const { mutateAsync: deleteDraft, isPending: isDeletingDraft } = useDeleteDraftLead();

// Delete and mark resolved composables
const { mutateAsync: deleteLead, isPending: isDeleting } = useDeleteLead();
const { mutateAsync: markLeadResolved, isPending: isMarkingResolved } = useMarkLeadResolved();

// Aside state for assigning experts
const asideOpen = ref(false);
const assigningLead = ref<LeadAdminResponse | null>(null);
const assignFormRef = useTemplateRef('assignFormRef');

// Form aside state (shared for both draft leads and leads)
const formAsideOpen = ref(false);
const editingDraft = ref<DraftLeadAdminResponse | null>(null);
const editingLead = ref<LeadAdminResponse | null>(null);
const draftFormRef = useTemplateRef('draftFormRef');
const leadFormRef = useTemplateRef('leadFormRef');

// Delete dialog state
const deleteDialogOpen = ref(false);
const leadToDelete = ref<LeadAdminResponse | null>(null);

// Draft lead delete dialog state
const deleteDraftDialogOpen = ref(false);
const draftToDelete = ref<DraftLeadAdminResponse | null>(null);

// Mark resolved dialog state
const markResolvedDialogOpen = ref(false);
const leadToMarkResolved = ref<LeadAdminResponse | null>(null);

function onAssignClick(lead: LeadAdminResponse) {
  assigningLead.value = lead;
  asideOpen.value = true;
}

function onAssignCancel() {
  assignFormRef.value?.resetForm();
  asideOpen.value = false;
  assigningLead.value = null;
}

function onAssignSuccess() {
  assignFormRef.value?.resetForm();
  asideOpen.value = false;
  assigningLead.value = null;
  toaster.create({
    type: 'success',
    title: 'Experts Assigned',
    description: 'The experts have been successfully assigned to the lead.',
  });
}

function onDeleteClick(lead: LeadAdminResponse) {
  leadToDelete.value = lead;
  deleteDialogOpen.value = true;
}

function onEditLeadClick(lead: LeadAdminResponse) {
  editingDraft.value = null;
  editingLead.value = lead;
  formAsideOpen.value = true;
}

function onLeadFormCancel() {
  leadFormRef.value?.resetForm();
  formAsideOpen.value = false;
  editingLead.value = null;
}

function onLeadFormSuccess() {
  leadFormRef.value?.resetForm();
  formAsideOpen.value = false;
  editingLead.value = null;
}

async function confirmDelete() {
  if (!leadToDelete.value) return;

  try {
    await deleteLead(leadToDelete.value.id);
    toaster.create({
      type: 'success',
      title: 'Lead Deleted',
      description: `The lead "${leadToDelete.value.project_title}" has been deleted.`,
    });
    deleteDialogOpen.value = false;
    leadToDelete.value = null;
  } catch (err) {
    console.error('[LeadsManagementPage] Error deleting lead:', err);
    toaster.create({
      type: 'error',
      title: 'Deletion Failed',
      description: 'Failed to delete the lead. Please try again.',
    });
  }
}

function onMarkResolvedClick(lead: LeadAdminResponse) {
  // Check if there are experts assigned - required for marking as resolved
  if (lead.matches.length === 0) {
    toaster.create({
      type: 'warning',
      title: 'Cannot Mark as Resolved',
      description: 'You must assign at least one expert before marking the lead as resolved.',
    });
    return;
  }

  leadToMarkResolved.value = lead;
  markResolvedDialogOpen.value = true;
}

async function confirmMarkResolved() {
  if (!leadToMarkResolved.value) return;

  const expertIds = leadToMarkResolved.value.matches.map(match => match.expert.id);

  try {
    await markLeadResolved({
      leadId: leadToMarkResolved.value.id,
      expertIds: expertIds,
    });
    toaster.create({
      type: 'success',
      title: 'Lead Marked as Resolved',
      description: `The lead "${leadToMarkResolved.value.project_title}" has been marked as matched/resolved.`,
    });
    markResolvedDialogOpen.value = false;
    leadToMarkResolved.value = null;
  } catch (err) {
    console.error('[LeadsManagementPage] Error marking lead as resolved:', err);
    toaster.create({
      type: 'error',
      title: 'Failed to Mark as Resolved',
      description: 'Failed to update the lead status. Please try again.',
    });
  }
}

// Filter leads based on tab (client-side filtering as backup)
const filteredLeads = computed(() => {
  if (activeTab.value === 'pending') {
    return leads.value.filter(lead => lead.status === 'pending');
  }
  return leads.value;
});

// ==================
// Draft Lead Handlers
// ==================

function onCreateDraftClick() {
  editingLead.value = null;
  editingDraft.value = null;
  formAsideOpen.value = true;
}

function onEditDraftClick(draft: DraftLeadAdminResponse) {
  editingLead.value = null;
  editingDraft.value = draft;
  formAsideOpen.value = true;
}

function onDraftFormCancel() {
  draftFormRef.value?.resetForm();
  formAsideOpen.value = false;
  editingDraft.value = null;
}

function onDraftFormSuccess() {
  draftFormRef.value?.resetForm();
  formAsideOpen.value = false;
  editingDraft.value = null;
}

async function onSubmitDraftClick(draft: DraftLeadAdminResponse) {
  if (!draft.is_complete) {
    toaster.create({
      type: 'warning',
      title: 'Cannot Submit',
      description: 'Please complete all required fields before submitting.',
    });
    return;
  }

  try {
    await submitDraft(draft.id);
  } catch (err) {
    console.error('[LeadsManagementPage] Error submitting draft:', err);
  }
}

function onDeleteDraftClick(draft: DraftLeadAdminResponse) {
  draftToDelete.value = draft;
  deleteDraftDialogOpen.value = true;
}

async function confirmDeleteDraft() {
  if (!draftToDelete.value) return;

  try {
    await deleteDraft(draftToDelete.value.id);
    deleteDraftDialogOpen.value = false;
    draftToDelete.value = null;
  } catch (err) {
    console.error('[LeadsManagementPage] Error deleting draft:', err);
  }
}

function handleRefreshDraftLeads() {
  refetchDraftLeads();
}

const infiniteScrollSentinelRef = useTemplateRef<HTMLElement>('infiniteScrollSentinelRef');
let infiniteObserver: IntersectionObserver | null = null;

const isLeadTab = computed(() => activeTab.value === 'pending' || activeTab.value === 'all');
const isDraftTab = computed(() => activeTab.value === 'draft-pending' || activeTab.value === 'draft-all');

const shouldShowInfiniteSentinel = computed(() => {
  if (isLeadTab.value) {
    return filteredLeads.value.length > 0 && (hasNextPage.value || isFetchingNextPage.value);
  }

  if (isDraftTab.value) {
    return draftLeads.value.length > 0 && (hasDraftNextPage.value || isDraftFetchingNextPage.value);
  }

  return false;
});

const isLoadingMore = computed(() => {
  if (isLeadTab.value) return isFetchingNextPage.value;
  if (isDraftTab.value) return isDraftFetchingNextPage.value;
  return false;
});

function maybeFetchNextPage() {
  if (isLeadTab.value && hasNextPage.value && !isFetchingNextPage.value) {
    fetchNextPage();
    return;
  }

  if (isDraftTab.value && hasDraftNextPage.value && !isDraftFetchingNextPage.value) {
    fetchNextDraftPage();
  }
}

onMounted(() => {
  infiniteObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        maybeFetchNextPage();
      }
    },
    {
      root: null,
      rootMargin: '200px',
      threshold: 0.1,
    }
  );

  if (infiniteScrollSentinelRef.value) {
    infiniteObserver.observe(infiniteScrollSentinelRef.value);
  }
});

watch(infiniteScrollSentinelRef, (newEl, oldEl) => {
  if (!infiniteObserver) return;
  if (oldEl) {
    infiniteObserver.unobserve(oldEl);
  }
  if (newEl) {
    infiniteObserver.observe(newEl);
  }
});

onUnmounted(() => {
  if (infiniteObserver && infiniteScrollSentinelRef.value) {
    infiniteObserver.unobserve(infiniteScrollSentinelRef.value);
  }
  infiniteObserver?.disconnect();
});
</script>

<template>
  <main class="flex-1 p-8 overflow-y-auto bg-secondary font-light w-full">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-3xl font-normal">Lead Management</h1>
        <p class="mt-1 text-gray-600">Manage and assign experts to client leads</p>
      </div>
      <UiButton variant="primary" label="Create Draft Lead" icon="plus-square" @click="onCreateDraftClick" />
    </div>

    <!-- Tabs with Refresh Button -->
    <div class="relative">
      <!-- Refresh Button (positioned at top right of tab bar, visible only for draft tabs) -->
      <div 
        v-if="activeTab === 'draft-pending' || activeTab === 'draft-all'" 
        class="absolute right-0 top-0 z-10"
      >
        <UiButton 
          variant="ghost" 
          label="Refresh"
          icon="repeat"
          :loading="isDraftRefetching"
          @click="handleRefreshDraftLeads"
        />
      </div>

      <UiTabs v-model="activeTab" default-value="pending">
      <UiTabsItem value="pending" label="Pending Leads">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-12">
          <UiText variant="p" as="p" text="Loading leads..." />
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-12">
          <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
            <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
              Failed to load leads
            </UiText>
            <UiText variant="p-small" as="p" class="text-red-600">
              {{ error.message || 'An error occurred while fetching leads' }}
            </UiText>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredLeads.length === 0" class="text-center py-12">
          <div class="flex flex-col items-center gap-4">
            <UiIcon name="package-placeholder" class="w-16 h-16 text-gray-400" />
            <div>
              <UiText variant="h6" as="h6" class="font-semibold mb-1">
                No pending leads
              </UiText>
              <UiText variant="p-small" as="p" class="text-gray-600">
                All leads have been matched with experts
              </UiText>
            </div>
          </div>
        </div>

        <!-- Leads List -->
        <div v-else class="flex flex-col gap-6">
          <LeadCard
            v-for="lead in filteredLeads"
            :key="lead.id"
            :lead="lead"
            @assign="onAssignClick"
            @edit="onEditLeadClick"
            @delete="onDeleteClick"
            @mark-resolved="onMarkResolvedClick"
          />
        </div>
      </UiTabsItem>

      <UiTabsItem value="all" label="All Leads">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-12">
          <UiText variant="p" as="p" text="Loading leads..." />
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-12">
          <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
            <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
              Failed to load leads
            </UiText>
            <UiText variant="p-small" as="p" class="text-red-600">
              {{ error.message || 'An error occurred while fetching leads' }}
            </UiText>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredLeads.length === 0" class="text-center py-12">
          <div class="flex flex-col items-center gap-4">
            <UiIcon name="package-placeholder" class="w-16 h-16 text-gray-400" />
            <div>
              <UiText variant="h6" as="h6" class="font-semibold mb-1">
                No leads found
              </UiText>
              <UiText variant="p-small" as="p" class="text-gray-600">
                There are no leads in the system yet
              </UiText>
            </div>
          </div>
        </div>

        <!-- Leads List -->
        <div v-else class="flex flex-col gap-6">
          <LeadCard
            v-for="lead in filteredLeads"
            :key="lead.id"
            :lead="lead"
            @assign="onAssignClick"
            @edit="onEditLeadClick"
            @delete="onDeleteClick"
            @mark-resolved="onMarkResolvedClick"
          />
        </div>
      </UiTabsItem>

      <!-- Draft Leads Tab (Pending) -->
      <UiTabsItem value="draft-pending" label="Draft Leads">
        <!-- Loading State -->
        <div v-if="isDraftLoading" class="text-center py-12">
          <UiText variant="p" as="p" text="Loading draft leads..." />
        </div>

        <!-- Error State -->
        <div v-else-if="draftError" class="text-center py-12">
          <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
            <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
              Failed to load draft leads
            </UiText>
            <UiText variant="p-small" as="p" class="text-red-600">
              {{ draftError.message || 'An error occurred while fetching draft leads' }}
            </UiText>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="draftLeads.length === 0" class="text-center py-12">
          <div class="flex flex-col items-center gap-4">
            <UiIcon name="package-placeholder" class="w-16 h-16 text-gray-400" />
            <div>
              <UiText variant="h6" as="h6" class="font-semibold mb-1">
                No draft leads
              </UiText>
              <UiText variant="p-small" as="p" class="text-gray-600">
                Create a draft lead to get started
              </UiText>
            </div>
            <UiButton variant="primary" @click="onCreateDraftClick">
              <template #before_label>
                <UiIcon name="plus-square" class="w-4 h-4" />
              </template>
              Create Draft Lead
            </UiButton>
          </div>
        </div>

        <!-- Draft Leads List -->
        <div v-else class="flex flex-col gap-6">
          <DraftLeadCard
            v-for="draft in draftLeads"
            :key="draft.id"
            :draft-lead="draft"
            @edit="onEditDraftClick"
            @submit="onSubmitDraftClick"
            @delete="onDeleteDraftClick"
          />
        </div>
      </UiTabsItem>

      <!-- All Draft Leads Tab -->
      <UiTabsItem value="draft-all" label="All Draft Leads">
        <!-- Loading State -->
        <div v-if="isDraftLoading" class="text-center py-12">
          <UiText variant="p" as="p" text="Loading draft leads..." />
        </div>

        <!-- Error State -->
        <div v-else-if="draftError" class="text-center py-12">
          <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
            <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
              Failed to load draft leads
            </UiText>
            <UiText variant="p-small" as="p" class="text-red-600">
              {{ draftError.message || 'An error occurred while fetching draft leads' }}
            </UiText>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="draftLeads.length === 0" class="text-center py-12">
          <div class="flex flex-col items-center gap-4">
            <UiIcon name="package-placeholder" class="w-16 h-16 text-gray-400" />
            <div>
              <UiText variant="h6" as="h6" class="font-semibold mb-1">
                No draft leads
              </UiText>
              <UiText variant="p-small" as="p" class="text-gray-600">
                Create a draft lead to get started
              </UiText>
            </div>
            <UiButton variant="primary" @click="onCreateDraftClick">
              <template #before_label>
                <UiIcon name="plus-square" class="w-4 h-4" />
              </template>
              Create Draft Lead
            </UiButton>
          </div>
        </div>

        <!-- Draft Leads List -->
        <div v-else class="flex flex-col gap-6">
          <DraftLeadCard
            v-for="draft in draftLeads"
            :key="draft.id"
            :draft-lead="draft"
            @edit="onEditDraftClick"
            @submit="onSubmitDraftClick"
            @delete="onDeleteDraftClick"
          />
        </div>
      </UiTabsItem>
    </UiTabs>

    <div
      v-if="shouldShowInfiniteSentinel"
      ref="infiniteScrollSentinelRef"
      class="h-14 flex items-center justify-center"
    >
      <UiText v-if="isLoadingMore" variant="p-small" as="p" class="text-gray-500">
        Loading more...
      </UiText>
    </div>
    </div>

    <!-- Assign Experts Aside -->
    <UiAside v-model="asideOpen" position="right" :close-on-overlay-click="false">
      <div class="px-4 py-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
        <div class="flex items-center justify-between">
          <UiText as="h3" variant="h3">
            Assign Experts
          </UiText>
          <UiButton variant="ghost" shape="square" icon="close" @click="onAssignCancel" />
        </div>
        <UiText 
          as="p" 
          class="text-gray-600 mb-4" 
          text="Search and select up to 3 experts to assign to this lead. The experts will be notified about the opportunity." 
        />
        
        <AssignExpertsForm 
          v-if="assigningLead"
          ref="assignFormRef"
          :lead-id="assigningLead.id"
          :lead-title="assigningLead.project_title"
          :current-matches-count="assigningLead.matches_count"
          :max-matches="assigningLead.max_matches"
          @success="onAssignSuccess"
          @cancel="onAssignCancel"
        />
      </div>
    </UiAside>

    <!-- Form Aside (shared for Draft Leads and Leads) -->
    <UiAside v-model="formAsideOpen" position="right" :close-on-overlay-click="false">
      <div class="px-4 py-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
        <!-- Draft Lead Form -->
        <template v-if="!editingLead">
          <div class="flex items-center justify-between">
            <UiText as="h3" variant="h3">
              {{ editingDraft ? 'Edit Draft Lead' : 'Create Draft Lead' }}
            </UiText>
            <UiButton variant="ghost" shape="square" icon="close" @click="onDraftFormCancel" />
          </div>
          <UiText 
            as="p" 
            class="text-gray-600 mb-4" 
            :text="editingDraft 
              ? 'Update the draft lead details below.' 
              : 'Create a new draft lead. You can save it and submit later, or submit immediately.'" 
          />
          
          <DraftLeadForm 
            ref="draftFormRef"
            :draft-lead="editingDraft"
            @success="onDraftFormSuccess"
            @cancel="onDraftFormCancel"
          />
        </template>

        <!-- Lead Edit Form -->
        <template v-else>
          <div class="flex items-center justify-between">
            <UiText as="h3" variant="h3">
              Edit Lead
            </UiText>
            <UiButton variant="ghost" shape="square" icon="close" @click="onLeadFormCancel" />
          </div>
          <UiText 
            as="p" 
            class="text-gray-600 mb-4" 
            text="Update the lead details below."
          />
          
          <LeadForm 
            ref="leadFormRef"
            :lead="editingLead"
            @success="onLeadFormSuccess"
            @cancel="onLeadFormCancel"
          />
        </template>
      </div>
    </UiAside>

    <!-- Delete Confirmation Dialog -->
    <UiDialog
      v-model="deleteDialogOpen"
      v-model:loading="isDeleting"
      title="Delete Lead"
      :text="`Are you sure you want to delete the lead &quot;${leadToDelete?.project_title}&quot;? This action cannot be undone.`"
      primary-action-label="Delete Lead"
      secondary-action-label="Cancel"
      size="sm"
      @primary-action="confirmDelete"
      @secondary-action="deleteDialogOpen = false"
    />

    <!-- Delete Draft Confirmation Dialog -->
    <UiDialog
      v-model="deleteDraftDialogOpen"
      v-model:loading="isDeletingDraft"
      title="Delete Draft Lead"
      :text="`Are you sure you want to delete the draft lead &quot;${draftToDelete?.project_title || 'Untitled Draft'}&quot;? This action cannot be undone.`"
      primary-action-label="Delete Draft"
      secondary-action-label="Cancel"
      size="sm"
      @primary-action="confirmDeleteDraft"
      @secondary-action="deleteDraftDialogOpen = false"
    />

    <!-- Mark Resolved Confirmation Dialog -->
    <UiDialog
      v-model="markResolvedDialogOpen"
      v-model:loading="isMarkingResolved"
      title="Mark Lead as Resolved"
      :text="`Are you sure you want to mark the lead &quot;${leadToMarkResolved?.project_title}&quot; as resolved? This will change its status to matched.`"
      primary-action-label="Mark as Resolved"
      secondary-action-label="Cancel"
      size="sm"
      @primary-action="confirmMarkResolved"
      @secondary-action="markResolvedDialogOpen = false"
    />
  </main>
</template>
